import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Field, Label } from './fieldset';
import { Eye, EyeOff, Shield, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useI18n } from '../hooks/useI18n';

interface NAVCredentials {
    id: string;
    cost_center_id?: string;
    company_id?: string;
    tax_number: string;
    is_active: boolean;
    last_used_at?: string;
    created_at: string;
}

interface NAVCredentialsFormData {
    login: string;
    password: string;
    tax_number: string;
    sign_key: string;
    exchange_key: string;
    is_active: boolean;
}

interface NAVCredentialsFormProps {
    costCenterId?: string;
    companyId?: string;
    onCredentialsChange?: (hasCredentials: boolean) => void;
}

export default function NAVCredentialsForm({ costCenterId, companyId, onCredentialsChange }: NAVCredentialsFormProps) {
    const { t } = useI18n();
    // Use companyId if provided, otherwise fall back to costCenterId
    const entityId = companyId || costCenterId;
    const isCompanyMode = !!companyId;
    const [credentials, setCredentials] = useState<NAVCredentials | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [testing, setTesting] = useState<boolean>(false);
    const [showForm, setShowForm] = useState<boolean>(false);
    const [showPasswords, setShowPasswords] = useState<boolean>(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

    const [formData, setFormData] = useState<NAVCredentialsFormData>({
        login: '',
        password: '',
        tax_number: '',
        sign_key: '',
        exchange_key: '',
        is_active: true
    });

    // Load existing credentials
    const loadCredentials = async () => {
        if (!entityId) return;

        try {
            setLoading(true);
            const response = await apiFetch(`/api/nav-credentials/${entityId}`);

            if (response.ok) {
                const data = await response.json();
                setCredentials(data);
                setFormData(prev => ({
                    ...prev,
                    tax_number: data.tax_number,
                    is_active: data.is_active
                }));
                onCredentialsChange?.(true);
            } else if (response.status === 404) {
                setCredentials(null);
                onCredentialsChange?.(false);
            } else {
                console.error('Failed to load NAV credentials');
            }
        } catch (error) {
            console.error('Error loading NAV credentials:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCredentials();
    }, [entityId]);

    // Save credentials
    const handleSave = async () => {
        if (!entityId) return;

        try {
            setSaving(true);
            setTestResult(null);

            const payload = isCompanyMode
                ? { ...formData, company_id: companyId }
                : { ...formData, cost_center_id: costCenterId };

            const url = credentials
                ? `/api/nav-credentials/${entityId}`
                : '/api/nav-credentials/';

            const method = credentials ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                await loadCredentials();
                setShowForm(false);
                // Clear sensitive form data
                setFormData(prev => ({
                    ...prev,
                    login: '',
                    password: '',
                    sign_key: '',
                    exchange_key: ''
                }));
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Failed to save NAV credentials');
            }
        } catch (error) {
            console.error('Error saving NAV credentials:', error);
            alert('Failed to save NAV credentials');
        } finally {
            setSaving(false);
        }
    };

    // Test credentials
    const handleTest = async () => {
        if (!entityId || !credentials) return;

        try {
            setTesting(true);
            setTestResult(null);

            const payload = isCompanyMode
                ? { company_id: companyId }
                : { cost_center_id: costCenterId };

            const response = await apiFetch('/api/nav-credentials/test', {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                const result = await response.json();
                setTestResult(result);
            } else {
                const errorData = await response.json();
                setTestResult({
                    success: false,
                    message: errorData.detail || 'Test failed'
                });
            }
        } catch (error) {
            console.error('Error testing NAV credentials:', error);
            setTestResult({
                success: false,
                message: 'Test failed due to network error'
            });
        } finally {
            setTesting(false);
        }
    };

    // Delete credentials
    const handleDelete = async () => {
        if (!entityId || !credentials) return;

        const entityType = isCompanyMode ? 'company' : 'cost center';
        if (!confirm(`Are you sure you want to delete the NAV credentials? This will disable NAV integration for this ${entityType}.`)) {
            return;
        }

        try {
            const response = await apiFetch(`/api/nav-credentials/${entityId}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                setCredentials(null);
                setTestResult(null);
                onCredentialsChange?.(false);
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Failed to delete NAV credentials');
            }
        } catch (error) {
            console.error('Error deleting NAV credentials:', error);
            alert('Failed to delete NAV credentials');
        }
    };

    if (loading) {
        return (
            <div className="space-y-3">
                <div className="flex items-center space-x-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{t('nav.integration', 'common')}</span>
                </div>
                <div className="text-sm text-muted-foreground">{t('nav.loading', 'common')}</div>
            </div>
        );
    }

    return (
        <div className="space-y-4 border-t pt-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{t('nav.integration', 'common')}</span>
                    {credentials && (
                        <Badge variant={credentials.is_active ? 'default' : 'secondary'}>
                            {credentials.is_active ? t('status.active', 'common') : t('status.inactive', 'common')}
                        </Badge>
                    )}
                </div>

                {credentials ? (
                    <div className="flex space-x-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleTest}
                            disabled={testing}
                        >
                            {testing ? t('nav.testing', 'common') : t('nav.test', 'common')}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowForm(!showForm)}
                        >
                            {showForm ? t('actions.cancel', 'common') : t('actions.edit', 'common')}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleDelete}
                            className="text-destructive hover:text-destructive/90"
                        >
                            {t('actions.delete', 'common')}
                        </Button>
                    </div>
                ) : (
                    <Button
                        size="sm"
                        onClick={() => setShowForm(true)}
                    >
                        {t('nav.addCredentials', 'common')}
                    </Button>
                )}
            </div>

            {/* Status Information */}
            {credentials && !showForm && (
                <div className="text-sm text-muted-foreground space-y-1">
                    <div>{t('nav.taxNumber', 'common')} {credentials.tax_number}</div>
                    {credentials.last_used_at && (
                        <div>{t('nav.lastUsed', 'common')} {new Date(credentials.last_used_at).toLocaleString()}</div>
                    )}
                    <div>{t('nav.created', 'common')} {new Date(credentials.created_at).toLocaleString()}</div>
                </div>
            )}

            {/* Test Result */}
            {testResult && (
                <div className={`flex items-center space-x-2 text-sm p-2 rounded ${testResult.success
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                    {testResult.success ? (
                        <CheckCircle className="h-4 w-4" />
                    ) : (
                        <XCircle className="h-4 w-4" />
                    )}
                    <span>{testResult.message}</span>
                </div>
            )}

            {/* Credentials Form */}
            {showForm && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <AlertCircle className="h-4 w-4" />
                        <span>{t('nav.encryptedNote', 'common')}</span>
                    </div>

                    <Field className="">
                        <Label htmlFor="nav-login" className="">{t('nav.userLogin', 'common')}</Label>
                        <Input
                            id="nav-login"
                            type="text"
                            value={formData.login}
                            onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                            placeholder="Your NAV user login"
                            required
                        />
                    </Field>

                    <Field className="">
                        <Label htmlFor="nav-password" className="">{t('nav.userPassword', 'common')}</Label>
                        <div className="relative">
                            <Input
                                id="nav-password"
                                type={showPasswords ? "text" : "password"}
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder="Your NAV user password"
                                required
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3"
                                onClick={() => setShowPasswords(!showPasswords)}
                            >
                                {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                        </div>
                    </Field>

                    <Field className="">
                        <Label htmlFor="nav-tax-number" className="">{t('nav.taxNumberRequired', 'common')}</Label>
                        <Input
                            id="nav-tax-number"
                            type="text"
                            value={formData.tax_number}
                            onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                            placeholder="Hungarian tax number (8 digits)"
                            required
                        />
                    </Field>

                    <Field className="">
                        <Label htmlFor="nav-sign-key" className="">{t('nav.signingKey', 'common')}</Label>
                        <Input
                            id="nav-sign-key"
                            type={showPasswords ? "text" : "password"}
                            value={formData.sign_key}
                            onChange={(e) => setFormData({ ...formData, sign_key: e.target.value })}
                            placeholder="NAV signing key"
                            required
                        />
                    </Field>

                    <Field className="">
                        <Label htmlFor="nav-exchange-key" className="">{t('nav.exchangeKey', 'common')}</Label>
                        <Input
                            id="nav-exchange-key"
                            type={showPasswords ? "text" : "password"}
                            value={formData.exchange_key}
                            onChange={(e) => setFormData({ ...formData, exchange_key: e.target.value })}
                            placeholder="NAV exchange key"
                            required
                        />
                    </Field>

                    <Field className="">
                        <Label className="">
                            <input
                                type="checkbox"
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                className="mr-2"
                            />
                            {t('status.active', 'common')}
                        </Label>
                    </Field>

                    <div className="flex space-x-2 pt-2">
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? t('actions.saving', 'common') : (credentials ? t('actions.update', 'common') : t('actions.save', 'common'))}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowForm(false);
                                setFormData(prev => ({
                                    ...prev,
                                    login: '',
                                    password: '',
                                    sign_key: '',
                                    exchange_key: ''
                                }));
                            }}
                        >
                            {t('actions.cancel', 'common')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
